/**
 * Table of contents component with scroll highlighting.
 * 
 * Features:
 * - Parse markdown headings (h1-h6)
 * - Nested list structure
 * - Click to scroll to heading
 * - Intersection Observer for current section highlighting
 * - Sticky positioning
 * - Collapsible on mobile
 * 
 * Called by:
 * - EssayDetailPage for navigation within essay
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Typography, Collapse } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import type { TocItem } from '@compendium/shared';

const { Text } = Typography;

// =============================================================================
// Props Interface
// =============================================================================

interface TableOfContentsProps {
  /** Markdown content to parse headings from */
  content: string;
  /** Container element for scrolling (default document) */
  scrollContainer?: HTMLElement | null;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Generate unique ID for heading anchor.
 * Converts heading text to kebab-case.
 */
function generateId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Parse markdown content and extract headings.
 * Returns flat list with heading level info.
 */
function parseHeadings(markdown: string): TocItem[] {
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  const items: TocItem[] = [];
  let match: RegExpExecArray | null;

  while ((match = headingRegex.exec(markdown)) !== null) {
    const hashMarks = match[1];
    const headingText = match[2];
    if (!hashMarks || !headingText) continue;
    const level = hashMarks.length;
    const text = headingText.trim();
    const id = generateId(text);

    items.push({
      id,
      text,
      level,
      children: [],
    });
  }

  return items;
}

/**
 * Build nested structure from flat heading list.
 * H1 contains H2s, H2 contains H3s, etc.
 */
function buildNestedToc(items: TocItem[]): TocItem[] {
  if (items.length === 0) return [];

  const result: TocItem[] = [];
  const stack: TocItem[] = [];

  for (const item of items) {
    const tocItem: TocItem = { ...item, children: [] };

    // Find parent by walking up the stack
    while (stack.length > 0) {
      const top = stack[stack.length - 1];
      if (top && top.level >= item.level) {
        stack.pop();
      } else {
        break;
      }
    }

    if (stack.length === 0) {
      // No parent, add to root
      result.push(tocItem);
    } else {
      // Add as child of top of stack
      const parent = stack[stack.length - 1];
      if (parent) {
        parent.children.push(tocItem);
      }
    }

    stack.push(tocItem);
  }

  return result;
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Table of contents component.
 * Displays a navigable list of headings from markdown content.
 */
function TableOfContents({ content, scrollContainer }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>('');

  // Parse and build TOC structure
  const tocItems = useMemo(() => {
    const flat = parseHeadings(content);
    return buildNestedToc(flat);
  }, [content]);

  // Flat list of IDs for intersection observer
  const allIds = useMemo(() => {
    const flat = parseHeadings(content);
    return flat.map((item) => item.id);
  }, [content]);

  // Set up intersection observer for scroll highlighting
  useEffect(() => {
    if (allIds.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the first visible heading
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          // Sort by position and take the topmost
          visibleEntries.sort((a, b) => {
            const aRect = a.boundingClientRect;
            const bRect = b.boundingClientRect;
            return aRect.top - bRect.top;
          });
          const first = visibleEntries[0];
          if (first) {
            setActiveId(first.target.id);
          }
        }
      },
      {
        root: scrollContainer || null,
        rootMargin: '-80px 0px -70% 0px',
        threshold: 0,
      }
    );

    // Observe all heading elements
    allIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [allIds, scrollContainer]);

  /**
   * Handle clicking on a TOC item.
   * Scrolls to the heading with smooth behavior.
   */
  const handleClick = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Update active ID immediately for responsiveness
      setActiveId(id);
    }
  }, []);

  /**
   * Render a TOC item and its children recursively.
   */
  function renderItem(item: TocItem, depth = 0): React.ReactNode {
    const isActive = item.id === activeId;

    return (
      <div key={item.id} style={{ marginLeft: depth * 12 }}>
        <div
          className={`sidebar-nav-item${isActive ? ' active' : ''}`}
          onClick={() => handleClick(item.id)}
        >
          <Text
            style={{
              fontSize: 13,
              color: isActive ? '#fff' : '#d9d9d9',
              fontWeight: isActive ? 500 : 400,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.text}
          </Text>
        </div>
        {item.children.length > 0 && (
          <div style={{ marginTop: 2 }}>
            {item.children.map((child) => renderItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  // Don't render if no headings
  if (tocItems.length === 0) {
    return null;
  }

  const tocContent = (
    <div
      style={{
        maxHeight: 'calc(100vh - 200px)',
        overflow: 'auto',
        padding: '8px 0',
      }}
    >
      {tocItems.map((item) => renderItem(item))}
    </div>
  );

  return (
    <div
      style={{
        position: 'sticky',
        top: 80,
        background: 'var(--bg-surface)',
        borderRadius: 8,
        border: '1px solid var(--border-primary)',
      }}
    >
      {/* Mobile: Collapsible */}
      <div className="toc-mobile" style={{ display: 'none' }}>
        <Collapse
          ghost
          expandIconPosition="end"
          items={[
            {
              key: 'toc',
              label: (
                <Text strong style={{ fontSize: 14 }}>
                  <MenuOutlined style={{ marginRight: 8 }} />
                  Table of Contents
                </Text>
              ),
              children: tocContent,
            },
          ]}
        />
      </div>

      {/* Desktop: Always visible */}
      <div className="toc-desktop" style={{ padding: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 14 }}>
          Table of Contents
        </Text>
        {tocContent}
      </div>

      {/* CSS for responsive behavior */}
      <style>{`
        @media (max-width: 768px) {
          .toc-desktop { display: none !important; }
          .toc-mobile { display: block !important; }
        }
        @media (min-width: 769px) {
          .toc-desktop { display: block !important; }
          .toc-mobile { display: none !important; }
        }
      `}</style>
    </div>
  );
}

export default TableOfContents;
